import { useState } from "react";
import type { FormEvent } from "react";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdaqoovp";

export default function Contact() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [formError, setFormError] = useState<string>("");
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      setAgreed(false);
      return;
    }
    formData.delete("contact_alt");

    const message = String(formData.get("message") || "");
    if (message.length > 500) {
      setFormError("문의 내용은 500자 이내로 작성해주세요.");
      return;
    }

    const payload: Record<string, string> = {};
    formData.forEach((v, k) => {
      payload[k] = String(v);
    });
    payload.agreed = agreed ? "동의함" : "동의하지 않음";

    setStatus("submitting");
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let parsed: { ok?: boolean; error?: string; next?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed.ok) {
        setStatus("success");
        formEl.reset();
        setAgreed(false);
      } else {
        setStatus("error");
        const serverMsg = parsed?.error || "";
        setFormError(
          serverMsg && serverMsg.length < 200
            ? serverMsg
            : "신청 접수에 실패했습니다. 잠시 후 다시 시도해주세요."
        );
      }
    } catch (err) {
      setStatus("error");
      setFormError("신청 접수에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div className="text-xs md:text-sm tracking-widest text-primary-500 font-semibold mb-4">
              REGISTRATION
            </div>
            <h2 className="font-heading font-black text-3xl md:text-5xl text-foreground-50 leading-tight tracking-tight">
              고객 정보 및
              <br />
              <span className="text-gold-gradient">등록 안내</span>
            </h2>
            <div className="gold-divider w-16 my-8"></div>
            <p className="text-base text-foreground-300 leading-relaxed">
              간단한 정보를 남겨주시면 전담 애널리스트가
              영업일 기준 1일 이내에 연락드립니다.
              상담 후 이용 여부는 온전히 회원님의 선택입니다.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-background-100 border border-background-300/60">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 tracking-wider">대표번호</div>
                  <a
                    href="tel:+827070080070"
                    className="text-base md:text-lg text-foreground-50 font-semibold hover:text-primary-500 transition-colors"
                  >
                    070-7008-0070
                  </a>
                  <div className="text-xs text-foreground-400 mt-0.5">평일 오전 9:00 ~ 오후 06:00</div>
                  <div className="text-xs text-foreground-400 mt-0.5">점심시간 오전 11:50 ~ 오후 13:00</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-background-100 border border-background-300/60">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 tracking-wider">이메일</div>
                  <a
                    href="mailto:gagury1@naver.com"
                    className="text-base md:text-lg text-foreground-50 font-semibold hover:text-primary-500 transition-colors"
                  >
                    gagury1@naver.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-background-100 border border-background-300/60">
                  <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 tracking-wider">본사 위치</div>
                  <div className="text-base md:text-lg text-foreground-50 font-semibold">
                    경기도 의정부시 세석로20 폴리프라자1 10층
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <div className="text-sm text-foreground-400 tracking-wider mb-4 font-semibold">입금 계좌</div>
              <div className="border border-background-300/50 rounded-xl overflow-hidden bg-background-100/80 backdrop-blur">
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-background-200 border border-background-300/60 flex-shrink-0">
                      <i className="ri-bank-line text-primary-500 text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm text-foreground-400 font-medium">KB국민은행</div>
                      <div className="text-lg md:text-xl text-foreground-100 font-extrabold tracking-wider mt-0.5">289301-04-264929</div>
                    </div>
                  </div>
                  <div className="border-t border-background-300/40 pt-4 mt-4">
                    <div className="text-xs text-foreground-400 mb-1 tracking-wide">예금주</div>
                    <div className="text-base md:text-lg text-foreground-50 font-extrabold">주식회사 프라임파트너스</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-100 border border-background-300/40 rounded-xl p-6 md:p-10">
            {status === "success" ? (
              <div className="min-h-[400px] flex flex-col items-center justify-center text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-500/40 mb-6">
                  <i className="ri-check-line text-primary-500 text-3xl"></i>
                </div>
                <h3 className="font-heading font-bold text-2xl text-foreground-50">
                  상담 신청이 완료되었습니다
                </h3>
                <p className="text-sm text-foreground-300 mt-3 max-w-sm">
                  확인 후 빠르게 연락드리겠습니다.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 border border-primary-500/60 text-primary-500 hover:bg-primary-500 hover:text-background-50 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                >
                  새 상담 신청하기
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                id="prime-consultation-form"
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-semibold text-foreground-50 mb-2">
                    성함 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full bg-background-50 border border-background-300/70 focus:border-primary-500 rounded-lg px-4 py-4 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-500"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-50 mb-2">
                    휴대폰 번호 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full bg-background-50 border border-background-300/70 focus:border-primary-500 rounded-lg px-4 py-4 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-500"
                    placeholder="010-0000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-50 mb-2">
                    문의 내용
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    className="w-full bg-background-50 border border-background-300/70 focus:border-primary-500 rounded-lg px-4 py-4 text-sm text-foreground-50 outline-none transition-colors resize-none placeholder:text-foreground-500"
                    placeholder="보유하신 종목의 현재 손실율을 입력하시거나, 궁금하신 종목을 남겨주세요."
                  />
                </div>

                <div className="field-alt-contact" aria-hidden="true">
                  <label>
                    Please leave this field empty
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </label>
                </div>

                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    id="privacy-agree"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-background-400/70 text-primary-500 focus:ring-primary-400 bg-background-50 cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="privacy-agree" className="text-xs text-foreground-400 leading-relaxed cursor-pointer select-none">
                    개인정보 수집·이용에 동의합니다. (
                    <a href="#footer" className="text-primary-500 hover:underline cursor-pointer font-semibold">개인정보처리방침</a>
                    )
                  </label>
                </div>

                {formError && (
                  <div className="text-xs text-red-400 bg-red-950/40 border border-red-900/60 rounded-lg px-4 py-3">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting" || !agreed}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 dark:text-foreground-950 px-6 py-5 rounded-full text-xl font-extrabold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      전송 중...
                    </>
                  ) : (
                    "제출"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-16 bg-background-100 border border-background-300/40 rounded-xl p-6 md:p-8">
          <div className="text-xs text-foreground-500 leading-relaxed space-y-3">
            <p>
              <strong className="text-foreground-300">주식회사 프라임파트너스</strong>
              &nbsp;|&nbsp; 사업자등록번호: 249-81-03770 &nbsp;|&nbsp; 대표: 도관인
            </p>
            <p>당사는 금융투자업자가 아닌 유사투자자문업자로 개별 투자상담과 자금운용이 불가능합니다.</p>
            <p>투자결과에 따라 투자원금의 손실이 발생할 수 있으며, 그 손실은 투자자에게 귀속됩니다.</p>
            <p>
              <a href="#" className="hover:text-primary-500 cursor-pointer underline underline-offset-2">
                [이용약관]
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}